import type { Ref } from '@vue/reactivity'
import { defineStore } from 'pinia'
import { useForm } from 'vee-validate'
import { z } from 'zod'
import { translate, useTranslations } from '~/composables/translate/translations'
import { usePersonalStore } from '~/stores/personal'

export const useRegistrationFormStore = defineStore('form', () => {
  // allows letters, hyphens and apostrophes
  const nameSchema = /^[a-zA-ZÀ-ÿ'\s-]+$/
  const FIRST_NAME_FIELD = 'firstName'
  const LAST_NAME_FIELD = 'lastName'
  const EMAIL_FIELD = 'email'
  const PHONE_NUMBER_FIELD = 'phoneNumber'
  const COUNTRY_OF_RESIDENCE_FIELD = 'countryOfResidence'
  const TERMS_AND_CONDITIONS_FIELD = 'termsAndConditions'

  function nameError(error: string) {
    return z.string({ error })
      .min(2, { error })
      .refine(value => nameSchema.test(value), error)
  }

  const registrationSchema = z.object({
    firstName: nameError(translate('error_first_name_mssing')),
    lastName: nameError(translate('error_last_name_missing')),
    email: z.email({ error: translate('error_email') }),
    phoneNumber: z.string({ error: translate('error_required_field') })
      .min(1, { message: translate('error_required_field') }),
    countryOfResidence: z.string({ error: translate('error_required_field') }),
    termsAndConditions: z.coerce.boolean().refine((bool: boolean) => bool, {
      error: translate('error_required_field'),
    }),
  })

  const personalStore = usePersonalStore()

  const {
    meta,
    validate,
    defineComponentBinds,
    errors,
    handleSubmit,
    isFieldTouched,
    setValues,
    setErrors,
    setTouched,
  } = useForm({
    validationSchema: registrationSchema,
    initialValues: {
      firstName: personalStore.firstName ?? undefined,
      lastName: personalStore.lastName ?? undefined,
      email: personalStore.emailAddress ?? undefined,
      phoneNumber: personalStore.phoneNumber ?? undefined,
      termsAndConditions: undefined,
      countryOfResidence: personalStore.countryOfResidence ?? undefined,
    },
  })

  function capitalize(word?: string): string | undefined {
    if (!word)
      return undefined
    return word[0].toUpperCase() + word.slice(1).toLowerCase()
  }

  function isFieldValid(fieldName: keyof FormErrors): boolean {
    return !!errors.value[fieldName] && isFieldTouched(fieldName)
  }

  function createErrorMessage(fieldName: keyof FormErrors): Ref<ErrorMessage> {
    return computed(() => {
      return (isFieldValid(fieldName))
        ? { error: errors.value[fieldName] }
        : { error: undefined }
    })
  }

  const firstName = defineComponentBinds(FIRST_NAME_FIELD)
  const lastName = defineComponentBinds(LAST_NAME_FIELD)
  const email = defineComponentBinds(EMAIL_FIELD)
  const phoneNumber = defineComponentBinds(PHONE_NUMBER_FIELD)
  const termsAndConditions = defineComponentBinds('termsAndConditions')
  const countryOfResidence = defineComponentBinds('countryOfResidence')

  const isFormValid = computed<boolean>(() => meta.value.valid)

  // Tracks whether the phone field has a non-empty but invalid value (format error).
  // Set by ContactForm.vue when CtPhoneNumber emits an invalid mid-typing value.
  const phoneNumberFormatInvalid = ref(false)

  // Manual validation trigger for phone number
  async function validatePhoneNumber() {
    await validate()
  }
  const isFirstNameTouched = computed<boolean>(() => isFieldTouched(FIRST_NAME_FIELD))
  const isLastNameTouched = computed<boolean>(() => isFieldTouched(LAST_NAME_FIELD))
  const isEmailTouched = computed<boolean>(() => isFieldTouched(EMAIL_FIELD))
  const isPhoneNumberTouched = computed<boolean>(() => isFieldTouched(PHONE_NUMBER_FIELD))
  const isCountryOfResidenceTouched = computed<boolean>(() => isFieldTouched(COUNTRY_OF_RESIDENCE_FIELD))
  const isTermsAndConditionsTouched = computed<boolean>(() => isFieldTouched(TERMS_AND_CONDITIONS_FIELD))

  const firstNameError = createErrorMessage(FIRST_NAME_FIELD)
  const lastNameError = createErrorMessage(LAST_NAME_FIELD)
  const emailError = createErrorMessage(EMAIL_FIELD)
  const countryOfResidenceError = createErrorMessage('countryOfResidence' as keyof FormErrors)
  const termsAndConditionsError = createErrorMessage('termsAndConditions' as keyof FormErrors)

  // We need to call the submission handler to trigger error message propagation
  const validateForm = handleSubmit(async (values) => {
    setValues({
      firstName: capitalize(values.firstName),
      lastName: capitalize(values.lastName),
    })
    await validate()
  })

  // Alternative validation that always runs and returns the result
  async function validateAndCheckForm() {
    // Touch all fields to show validation errors
    setTouched({
      firstName: true,
      lastName: true,
      email: true,
      phoneNumber: true,
      countryOfResidence: true,
      termsAndConditions: true,
    })

    const result = await validate()
    return result.valid
  }

  // Reset touched state for details-page fields only (not email/phone which belong to contact sub-view)
  function resetDetailsTouched() {
    setTouched({
      firstName: false,
      lastName: false,
      countryOfResidence: false,
      termsAndConditions: false,
    })
  }

  // Change language of error message after field has already been validated
  watch(useTranslations().language, () => {
    setErrors({
      firstName: isFieldValid('firstName') ? translate('error_first_name_mssing') : '',
      lastName: isFieldValid('lastName') ? translate('error_last_name_missing') : '',
      email: isFieldValid('email') ? translate('error_email') : '',
      termsAndConditions: isFieldValid('termsAndConditions') ? translate('error_required_field') : '',
      phoneNumber: isFieldValid('phoneNumber') ? translate('error_required_field') : '',
      countryOfResidence: isFieldValid('countryOfResidence') ? translate('error_required_field') : '',
    })
  })

  return {
    errors,
    firstName,
    firstNameError,
    isFirstNameTouched,
    lastName,
    lastNameError,
    isLastNameTouched,
    email,
    emailError,
    isEmailTouched,
    phoneNumber,
    isPhoneNumberTouched,
    phoneNumberFormatInvalid,
    termsAndConditions,
    termsAndConditionsError,
    isTermsAndConditionsTouched,
    countryOfResidence,
    countryOfResidenceError,
    isCountryOfResidenceTouched,
    validateForm,
    validatePhoneNumber,
    validateAndCheckForm,
    resetDetailsTouched,
    setTouched,
    setValues,
    isFormValid,
    capitalize,
  }
})

interface ErrorMessage {
  error: string | undefined
}

interface FormErrors {
  firstName?: string
  lastName?: string
  email?: string
  countryOfResidence?: string
  termsAndConditions?: string
  phoneNumber?: string
}
